import React, { useMemo, useRef } from 'react';
import config from '@config/index';
import { useApp, useAppDispatch } from '@context/AppContext';
import { CommunicationsPreferencesWizardDrawer } from '@features/user/components/communications-preferences-wizard';
import { UserProfileFormDrawer } from '@features/user/components/user-profile-form';
import { UserProfile } from '@goodfynd/dashboard';
import type { DrawerRefMethods } from '@goodfynd/hooks';
import { ColorSchemeToggle } from '@goodfynd/layout';
import { useRouter } from 'next/router';

import type { DashboardUserProfileProps } from './types';

export const DashboardUserProfile = ({
  options = [],
}: DashboardUserProfileProps) => {
  const { isLoggedIn, user } = useApp();
  const { logout } = useAppDispatch();
  const router = useRouter();

  const formRef = useRef<DrawerRefMethods>(null);
  const communicationsPreferencesRef = useRef<DrawerRefMethods>(null);

  const navLinks = useMemo(() => {
    const navLinks: DashboardUserProfileProps['options'] = [];

    if (isLoggedIn) {
      navLinks.push(
        {
          label: 'Personal info',
          onClick: () => formRef.current?.open(),
        },
        {
          divider: true,
        },
        {
          label: 'Communications',
          onClick: () => communicationsPreferencesRef.current?.open(),
        },
        {
          divider: true,
        }
      );

      navLinks.push(
        { label: <ColorSchemeToggle color="neutral" /> },
        {
          divider: true,
        }
      );
    } else {
      navLinks.push(
        {
          href: config.routes.account.signIn,
          label: 'Log in',
          onClick: () => {
            window.location.href = config.routes.account.signIn;
          },
        },
        {
          href: config.routes.account.signUp,
          label: 'Sign up',
          onClick: () => {
            window.location.href = config.routes.account.signUp;
          },
        }
      );
    }

    navLinks.push(
      ...options.map((option) => ({
        ...option,
        onClick: () => {
          if (option.href) {
            router.push(option.href);
          }
        },
      })),
      {
        hide: !isLoggedIn,
        label: 'Logout',
        onClick: () => logout(),
      }
    );

    return navLinks;
  }, [isLoggedIn, logout, options, router]);

  return (
    <div>
      <UserProfile options={navLinks} user={user} />

      <CommunicationsPreferencesWizardDrawer
        hideTarget
        ref={communicationsPreferencesRef}
      />

      <UserProfileFormDrawer hideTarget ref={formRef} />
    </div>
  );
};
